<template>
  <div>
    <!-- Profile card header -->
    <div class="bg-white rounded-xl border border-gray-200/60 mb-6">
      <div class="px-6 py-6 flex items-center gap-5">
        <div class="w-16 h-16 rounded-full bg-[var(--color-primary-light)] text-[var(--color-primary)] flex items-center justify-center text-xl font-semibold shrink-0">
          {{ userInitials }}
        </div>
        <div class="flex-1 min-w-0">
          <h2 class="text-lg font-semibold text-gray-900 truncate">{{ displayName }}</h2>
          <p class="text-sm text-gray-400 truncate">{{ user?.email }}</p>
          <div class="flex items-center gap-2 mt-2">
            <span v-if="isAdmin" class="inline-flex items-center px-2.5 py-0.5 rounded-md text-[11px] font-semibold uppercase tracking-wider bg-[var(--color-primary-light)] text-[var(--color-primary)]">
              Admin
            </span>
            <span class="inline-flex items-center px-2.5 py-0.5 rounded-md text-[11px] font-semibold uppercase tracking-wider bg-green-50 text-green-600">
              Active
            </span>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="px-6 border-t border-gray-100">
        <div class="flex gap-0">
          <button
            @click="activeTab = 'general'"
            :class="[
              'relative px-5 py-3.5 text-sm font-medium transition-colors',
              activeTab === 'general'
                ? 'text-[var(--color-primary)]'
                : 'text-gray-400 hover:text-gray-600'
            ]"
          >
            General
            <span v-if="activeTab === 'general'" class="absolute bottom-0 left-0 right-0 h-[2px] bg-[var(--color-primary)] rounded-full" />
          </button>
          <button
            @click="activeTab = 'security'"
            :class="[
              'relative px-5 py-3.5 text-sm font-medium transition-colors',
              activeTab === 'security'
                ? 'text-[var(--color-primary)]'
                : 'text-gray-400 hover:text-gray-600'
            ]"
          >
            Security
            <span v-if="activeTab === 'security'" class="absolute bottom-0 left-0 right-0 h-[2px] bg-[var(--color-primary)] rounded-full" />
          </button>
        </div>
      </div>
    </div>

    <!-- General tab -->
    <div v-if="activeTab === 'general'">
      <div class="bg-white rounded-xl border border-gray-200/60">
        <div class="px-6 py-3 border-b border-gray-100">
          <h3 class="text-[15px] font-semibold text-gray-900">Basic Information</h3>
          <p class="text-sm text-gray-400 mt-0.5">Update your personal details</p>
        </div>

        <form @submit.prevent="saveProfile" class="p-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label class="block text-sm font-medium text-gray-600 mb-1.5">First Name</label>
              <input
                v-model="form.first_name"
                type="text"
                required
                placeholder="Enter first name"
                class="w-full h-[42px] px-3.5 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder-gray-300 outline-none transition-all duration-150 focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary-glow)]"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-600 mb-1.5">Last Name</label>
              <input
                v-model="form.last_name"
                type="text"
                required
                placeholder="Enter last name"
                class="w-full h-[42px] px-3.5 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder-gray-300 outline-none transition-all duration-150 focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary-glow)]"
              />
            </div>
          </div>

          <div class="mt-5">
            <label class="block text-sm font-medium text-gray-600 mb-1.5">Email</label>
            <input
              :value="form.email"
              type="email"
              disabled
              class="w-full h-[42px] px-3.5 rounded-lg border border-gray-100 text-sm text-gray-400 bg-gray-50 cursor-not-allowed"
            />
            <p class="text-xs text-gray-400 mt-1.5">Email cannot be changed. Contact an administrator.</p>
          </div>

          <div class="flex justify-end mt-6 pt-5 border-t border-gray-100">
            <button
              type="submit"
              :disabled="savingProfile"
              class="btn-primary"
            >
              <svg v-if="!savingProfile" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
              </svg>
              {{ savingProfile ? 'Saving...' : 'Save Changes' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Security tab -->
    <div v-if="activeTab === 'security'">
      <div class="bg-white rounded-xl border border-gray-200/60">
        <div class="px-6 py-3 border-b border-gray-100">
          <h3 class="text-[15px] font-semibold text-gray-900">Change Password</h3>
          <p class="text-sm text-gray-400 mt-0.5">Update your password to keep your account secure</p>
        </div>

        <form @submit.prevent="changePassword" class="p-6">
          <div class="max-w-md space-y-5">
            <!-- Current password -->
            <div>
              <label class="block text-sm font-medium text-gray-600 mb-1.5">Current Password</label>
              <PasswordInput v-model="passwordForm.current_password" placeholder="Enter your current password" :required="true" />
            </div>

            <div class="border-t border-gray-100" />

            <!-- New password -->
            <div>
              <label class="block text-sm font-medium text-gray-600 mb-1.5">New Password</label>
              <PasswordInput v-model="passwordForm.password" placeholder="Enter new password" :required="true" />

              <!-- Strength meter -->
              <div class="mt-3">
                <div class="flex items-center justify-between mb-1.5">
                  <span class="text-xs font-medium text-gray-500">Password strength</span>
                  <span :class="['text-xs font-semibold', strengthColor]">{{ strengthLabel }}</span>
                </div>
                <div class="flex gap-1.5">
                  <div
                    v-for="i in 4"
                    :key="i"
                    class="h-1.5 flex-1 rounded-full transition-all duration-300"
                    :class="i <= strengthLevel ? strengthBarColor : 'bg-gray-100'"
                  />
                </div>
              </div>

              <!-- Password rules -->
              <div class="mt-3 space-y-1.5">
                <div v-for="rule in passwordRules" :key="rule.label" class="flex items-center gap-2">
                  <div :class="['w-4 h-4 rounded-full flex items-center justify-center shrink-0', rule.met ? 'bg-green-100' : 'bg-gray-100']">
                    <svg v-if="rule.met" class="w-2.5 h-2.5 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    <div v-else class="w-1.5 h-1.5 rounded-full bg-gray-300" />
                  </div>
                  <span :class="['text-xs', rule.met ? 'text-green-600' : 'text-gray-400']">{{ rule.label }}</span>
                </div>
              </div>
            </div>

            <!-- Confirm password -->
            <div>
              <label class="block text-sm font-medium text-gray-600 mb-1.5">Confirm New Password</label>
              <PasswordInput v-model="passwordForm.confirm_password" placeholder="Confirm new password" :required="true" />
              <p v-if="passwordForm.confirm_password && !passwordsMatch" class="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                Passwords do not match
              </p>
              <p v-else-if="passwordForm.confirm_password && passwordsMatch" class="text-xs text-green-600 mt-1.5 flex items-center gap-1">
                <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                Passwords match
              </p>
            </div>

            <!-- Error -->
            <div v-if="passwordError" class="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-100">
              <svg class="w-4 h-4 text-red-500 mt-0.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
              <span class="text-sm text-red-600">{{ passwordError }}</span>
            </div>
          </div>

          <div class="flex justify-end mt-6 pt-5 border-t border-gray-100">
            <button
              type="submit"
              :disabled="savingPassword || !canSubmitPassword"
              class="btn-primary"
            >
              <svg v-if="!savingPassword" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
              </svg>
              {{ savingPassword ? 'Updating...' : 'Update Password' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import PasswordInput from '../../components/PasswordInput.vue'
import api from '../../services/api.js'
import { notifySuccess, notifyError } from '../../services/notifications.js'

export default {
  name: 'ProfileView',
  components: { PasswordInput },

  data() {
    return {
      activeTab: 'general',
      form: {
        first_name: '',
        last_name: '',
        email: ''
      },
      passwordForm: {
        current_password: '',
        password: '',
        confirm_password: ''
      },
      passwordError: null,
      savingProfile: false,
      savingPassword: false,
      userId: null
    }
  },

  computed: {
    user() {
      return this.$store.getters['auth/user']
    },
    isAdmin() {
      return this.$store.getters['auth/isAdmin']
    },
    displayName() {
      if (!this.user) return ''
      return this.user.name || `${this.form.first_name} ${this.form.last_name}`.trim() || ''
    },
    userInitials() {
      const name = this.displayName
      if (!name) return '?'
      const parts = name.trim().split(' ')
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
      return name.substring(0, 2).toUpperCase()
    },
    passwordRules() {
      const p = this.passwordForm.password
      return [
        { label: 'At least 8 characters', met: p.length >= 8 },
        { label: 'One uppercase letter (A-Z)', met: /[A-Z]/.test(p) },
        { label: 'One lowercase letter (a-z)', met: /[a-z]/.test(p) },
        { label: 'One number (0-9)', met: /\d/.test(p) },
        { label: 'One special character (!@#$...)', met: /[^A-Za-z0-9]/.test(p) }
      ]
    },
    strengthLevel() {
      return this.passwordRules.filter(r => r.met).length
    },
    strengthLabel() {
      const labels = ['', 'Very weak', 'Weak', 'Fair', 'Strong', 'Very strong']
      return labels[this.strengthLevel] || ''
    },
    strengthColor() {
      const colors = ['', 'text-red-500', 'text-orange-500', 'text-amber-500', 'text-blue-500', 'text-green-600']
      return colors[this.strengthLevel] || ''
    },
    strengthBarColor() {
      const colors = ['', 'bg-red-400', 'bg-orange-400', 'bg-amber-400', 'bg-blue-400', 'bg-green-500']
      return colors[this.strengthLevel] || 'bg-gray-100'
    },
    passwordsMatch() {
      return this.passwordForm.password === this.passwordForm.confirm_password
    },
    canSubmitPassword() {
      return this.passwordForm.current_password &&
        this.strengthLevel >= 3 &&
        this.passwordsMatch &&
        this.passwordForm.password.length >= 8
    }
  },

  async mounted() {
    await this.loadProfile()
  },

  methods: {
    async loadProfile() {
      try {
        const res = await api.get('/auth/me')
        const user = res.data?.user
        if (user) {
          this.userId = user._id
          this.form.first_name = user.first_name || user.name?.split(' ')[0] || ''
          this.form.last_name = user.last_name || user.name?.split(' ').slice(1).join(' ') || ''
          this.form.email = user.email || ''
        }
      } catch {
        // user data already in store
        if (this.user) {
          this.form.first_name = this.user.first_name || this.user.name?.split(' ')[0] || ''
          this.form.last_name = this.user.last_name || this.user.name?.split(' ').slice(1).join(' ') || ''
          this.form.email = this.user.email || ''
        }
      }
    },

    async saveProfile() {
      this.savingProfile = true
      try {
        const endpoint = this.userId ? `/users/${this.userId}` : '/auth/me'
        await api.put(endpoint, {
          first_name: this.form.first_name,
          last_name: this.form.last_name
        })

        // Update store
        this.$store.commit('auth/SET_USER', {
          ...this.user,
          first_name: this.form.first_name,
          last_name: this.form.last_name,
          name: `${this.form.first_name} ${this.form.last_name}`
        })

        notifySuccess('Profile updated successfully')
      } catch (err) {
        notifyError(err?.message || 'Failed to update profile')
      } finally {
        this.savingProfile = false
      }
    },

    async changePassword() {
      this.passwordError = null

      if (!this.canSubmitPassword) return

      this.savingPassword = true
      try {
        const endpoint = this.userId ? `/users/${this.userId}` : '/auth/me'
        await api.put(endpoint, {
          current_password: this.passwordForm.current_password,
          password: this.passwordForm.password
        })

        this.passwordForm.current_password = ''
        this.passwordForm.password = ''
        this.passwordForm.confirm_password = ''
        notifySuccess('Password updated successfully')
      } catch (err) {
        this.passwordError = err?.message || 'Failed to update password'
      } finally {
        this.savingPassword = false
      }
    }
  }
}
</script>
